import React, { createContext, useState, useEffect, useContext } from 'react';
import { supabase } from '../lib/supabase';
import { saveLastLoginEmail } from '../lib/loginEmailStorage';
import { isGithubLoginConfigured } from '../lib/githubAuthAllowlist';
import { enforceGithubSessionAllowlist } from '../lib/enforceGithubSession';
import { User, Session } from '../types';
import { toast } from 'sonner';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGitHub: () => Promise<void>;
  signOut: () => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    void (async () => {
      const {
        data: { user: validatedUser },
        error: userError,
      } = await supabase.auth.getUser();
      if (userError || !validatedUser) {
        await supabase.auth.signOut();
        setSession(null);
        setUser(null);
        setIsLoading(false);
        return;
      }

      const {
        data: { session: initial },
      } = await supabase.auth.getSession();
      const allowed = await enforceGithubSessionAllowlist(initial, { toastOnReject: false });
      setSession(allowed as Session | null);
      setUser(allowed?.user ?? null);
      setIsLoading(false);
    })();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, nextSession) => {
      const allowed = await enforceGithubSessionAllowlist(nextSession, {
        toastOnReject: event === 'SIGNED_IN',
      });

      if (event === 'SIGNED_IN' && allowed?.user?.email) {
        saveLastLoginEmail(allowed.user.email);
      }

      setSession(allowed as Session | null);
      setUser(allowed?.user ?? null);
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      saveLastLoginEmail(email);
      toast.success('Signed in successfully');
    } catch (error: unknown) {
      console.error('Sign in error:', error);
      throw error;
    }
  };

  const signInWithGitHub = async () => {
    if (!isGithubLoginConfigured()) {
      throw new Error('GitHub sign-in is not configured.');
    }

    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'github',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          scopes: 'read:user user:email',
        },
      });
      if (error) throw error;
    } catch (error: unknown) {
      console.error('GitHub sign in error:', error);
      throw error;
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/login`,
        },
      });

      if (error) throw error;

      toast.success('Sign up successful! Please check your email to confirm your account.');
    } catch (error: unknown) {
      console.error('Sign up error:', error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      toast.success('Signed out successfully');
    } catch (error: unknown) {
      console.error('Sign out error:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, isLoading, signIn, signInWithGitHub, signOut, signUp }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
